/**
 * Lo pendiente del club, en palabras del panel · 13-10-2026
 *
 * `GET /clubs/{club}/today` manda códigos con su cuenta; aquí cada código tiene su
 * frase, su icono y **la pantalla donde se resuelve**. Vive en el panel y no en el
 * servidor porque las rutas son del panel: si una pantalla cambia de sitio, se toca
 * aquí y nada más.
 *
 * La frase dice qué pasa y por qué importa, en una línea; el botón, qué hacer. Es
 * lo que pidió el primer club: «que me diga cuántas cosas son y al pulsar me lleve
 * a actuar».
 */
import { cuenta, euros, fecha } from '@/shared/ui/formato';
import type { Pendiente } from '@/shared/ui/pendientes/tipos';
import {
  puedeFirmarElContrato,
  puedeGestionarPersonal,
  type Rol,
} from '@/club/permisos';

export interface PendienteDelClub {
  readonly code: string;
  readonly severity: 'high' | 'medium' | 'low';
  readonly count: number;
  readonly amount_cents: number | null;
  readonly due_on: string | null;
}

/** Lo que el club tiene montado: de aquí salen los «Primeros pasos» de Inicio. */
export interface MontajeDelClub {
  readonly venues: number;
  readonly groups: number;
  readonly groups_without_schedule: number;
  readonly fee_plans: number;
  readonly students: number;
  readonly profile_published: boolean;
}

export interface HoyDelClub {
  readonly pending: readonly PendienteDelClub[];
  readonly setup: MontajeDelClub;
  readonly summary: {
    readonly students_active: number;
    readonly sessions_today: number;
    readonly guardians: number;
    readonly guardians_with_app: number;
    readonly month: {
      readonly period: string;
      readonly issued_cents: number;
      readonly paid_cents: number;
      readonly pending_cents: number;
    };
  };
}

export function aPendientes(
  lista: readonly PendienteDelClub[],
  rol: Rol
): readonly Pendiente[] {
  /*
   * «Personal» es solo del dueño. El servidor ya no le manda al administrativo lo
   * que se resuelve ahí; si algún día lo hiciera, la tarjeta informa sin botón en
   * vez de llevarle a una pantalla que le devuelve.
   */
  const personal = puedeGestionarPersonal(rol) ? '/club/personal' : undefined;

  return lista.flatMap((p): Pendiente[] => {
    const n = p.count;
    const base = { clave: p.code, gravedad: p.severity };

    switch (p.code) {
      case 'dpa_unsigned':
        return puedeFirmarElContrato(rol)
          ? [
              {
                ...base,
                icono: 'contrato',
                titulo: 'Firma el contrato de encargo',
                detalle:
                  'Sin él, guardar los datos de tus alumnos no tiene base legal. Se lee y se firma en medio minuto.',
                accion: 'Firmarlo',
                destino: '/cumplimiento/contrato',
              },
            ]
          : [
              /*
               * Sin destino: la pantalla del contrato es del dueño y al
               * administrativo le devolvería a los consentimientos.
               */
              {
                ...base,
                icono: 'contrato',
                titulo: 'El contrato de encargo está sin firmar',
                detalle:
                  'Lo firma el dueño del club desde su panel. Recuérdaselo: sin él, guardar los datos de los alumnos no tiene base legal.',
                accion: '',
              },
            ];
      case 'payment_due':
        return [
          p.severity === 'high'
            ? {
                ...base,
                icono: 'euro',
                titulo: 'El panel está en solo lectura',
                detalle:
                  'El pago a Kiap está vencido. La app del maestro y la de las familias siguen funcionando.',
                accion: 'Ver el plan',
                destino: '/club/datos',
              }
            : {
                ...base,
                icono: 'euro',
                titulo: 'Tenéis un pago pendiente con Kiap',
                detalle:
                  'Todo sigue funcionando durante los días de gracia. Ponte al día para que no pase a solo lectura.',
                accion: 'Ver el plan',
                destino: '/club/datos',
              },
        ];
      case 'trial_bookings':
        return [
          {
            ...base,
            icono: 'invitar',
            titulo:
              n === 1
                ? 'Una familia quiere probar una clase'
                : `${n} familias quieren probar una clase`,
            detalle:
              'Contesta hoy: a los tres días esa familia ya se ha apuntado en otro sitio.',
            accion: 'Contestar',
            destino: '/alumnos/pruebas',
          },
        ];
      case 'data_requests':
        return [
          {
            ...base,
            icono: 'escudo',
            titulo:
              n === 1
                ? 'Una solicitud de datos por responder'
                : `${n} solicitudes de datos por responder`,
            detalle: p.due_on
              ? `La más próxima vence el ${fecha(p.due_on)}. La ley da un mes para responder.`
              : 'La ley da un mes para responder.',
            accion: 'Responder',
            destino: '/cumplimiento/solicitudes',
          },
        ];
      case 'overdue_invoices':
        return [
          {
            ...base,
            icono: 'cobros',
            titulo: `${cuenta(n, 'recibo vencido', 'recibos vencidos')} sin cobrar`,
            detalle: `${euros(p.amount_cents ?? 0)} pendientes. Llama primero a las deudas más antiguas.`,
            accion: 'Ver quién debe',
            destino: '/cobros/morosos',
          },
        ];
      case 'billing_run':
        return [
          {
            ...base,
            icono: 'remesa',
            titulo: 'La remesa de este mes no se ha emitido',
            detalle:
              'Los recibos del mes salen solos el día 1. Si no salieron, lánzala tú.',
            accion: 'Ir a la remesa',
            destino: '/cobros/remesa',
          },
        ];
      case 'sessions_missing':
        return [
          {
            ...base,
            icono: 'calendario',
            titulo:
              n === 1
                ? 'Un grupo no tiene clases esta semana'
                : `${n} grupos no tienen clases esta semana`,
            detalle: 'Sin clases generadas no se puede pasar lista.',
            accion: 'Generar las clases',
            destino: '/calendario/clases',
          },
        ];
      case 'second_hand_reported':
        return [
          {
            ...base,
            icono: 'material',
            titulo:
              n === 1
                ? 'Un anuncio de segunda mano denunciado'
                : `${n} anuncios de segunda mano denunciados`,
            detalle:
              'Una familia ha avisado. Tú decides si se queda en el tablón.',
            accion: 'Revisarlos',
            destino: '/material/segunda-mano',
          },
        ];
      case 'unread_messages':
        return [
          {
            ...base,
            icono: 'mensaje',
            titulo:
              n === 1
                ? 'Una conversación con mensajes sin leer'
                : `${n} conversaciones con mensajes sin leer`,
            detalle: 'Las familias esperan respuesta.',
            accion: 'Leerlos',
            destino: '/avisos/chat',
          },
        ];
      case 'consents_missing':
        return [
          {
            ...base,
            icono: 'cumplimiento',
            titulo: `${cuenta(n, 'alumno', 'alumnos')} con consentimientos sin firmar`,
            detalle:
              'Los firma la familia desde su app; aquí ves a quién le falta y se lo recuerdas.',
            accion: 'Ver a quién',
            destino: '/cumplimiento/consentimientos',
          },
        ];
      case 'licenses_expiring':
        return [
          {
            ...base,
            icono: 'competicion',
            titulo: `${cuenta(n, 'licencia federativa caduca', 'licencias federativas caducan')} en menos de un mes`,
            detalle: 'Renuévalas antes del próximo campeonato.',
            accion: 'Ver licencias',
            destino: '/competicion/licencias',
          },
        ];
      case 'documents_expiring':
        return [
          {
            ...base,
            icono: 'documento',
            titulo: `${cuenta(n, 'documento del equipo caduca', 'documentos del equipo caducan')} pronto`,
            detalle:
              'Seguros, certificados y titulaciones del personal, caducados o a menos de un mes.',
            accion: 'Revisarlos',
            destino: personal,
          },
        ];
      case 'discounts_expiring':
        return [
          {
            ...base,
            icono: 'descuento',
            titulo: `${cuenta(n, 'descuento vence', 'descuentos vencen')} en dos semanas`,
            detalle: 'A esas familias les subirá la cuota el mes que viene.',
            accion: 'Ver descuentos',
            destino: '/cobros/descuentos',
          },
        ];
      case 'venues_imprecise':
        return [
          {
            ...base,
            icono: 'sede',
            titulo:
              n === 1
                ? 'Una sede mal situada en el mapa'
                : `${n} sedes mal situadas en el mapa`,
            detalle:
              'Solo por código postal: las distancias que ve una familia pueden fallar en un kilómetro.',
            accion: 'Afinar la dirección',
            destino: '/club/sedes',
          },
        ];
      case 'staff_invited':
        return [
          {
            ...base,
            icono: 'invitar',
            titulo: `${cuenta(n, 'persona del equipo', 'personas del equipo')} sin entrar`,
            detalle:
              'Las invitaste hace más de tres días. Puedes volver a invitarlas desde Personal.',
            accion: 'Ver el equipo',
            destino: personal,
          },
        ];
      case 'profile_unpublished':
        return [
          {
            ...base,
            icono: 'buscador',
            titulo: 'Tu ficha del buscador no está publicada',
            detalle:
              'Publícala para que te encuentren familias nuevas en kiapclubs.com.',
            accion: 'Publicarla',
            destino: '/club/ficha',
          },
        ];
      default:
        // Un código nuevo del servidor que este panel aún no conoce no se pinta:
        // mejor nada que una tarjeta sin texto ni destino.
        return [];
    }
  });
}
